import React from 'react';
import {View} from 'react-native';
import {makeStyles} from '@rn-vui/themed';

import {Icon} from '../Icon';
import Text from '../themed/Text';
import PressableWithOpacityFeedback from '../ui-controls/PressableWithOpacityFeedback';

type StoreNameCardProps = {
  storeName: string;
  canDeliver?: boolean;
  canPickup?: boolean;
  onStoreNamePress?: () => void;
};

/**
 * Used on cart screen - displays store name (tappable) and delivery method availability (info only)
 */
function StoreNameCard({storeName, onStoreNamePress, canDeliver, canPickup}: StoreNameCardProps) {
  const styles = useStyles();

  return (
    <View style={[styles.containerStyle]}>
      <View style={[styles.wrapperStyle]}>
        {/* Store name - tappable to change store */}
        <PressableWithOpacityFeedback onPress={onStoreNamePress} style={styles.storeContainerStyle}>
          <Text
            style={[styles.textStyle, styles.storeNameTextStyle]}
            numberOfLines={1}
            ellipsizeMode="tail">
            {storeName}
          </Text>
          <Icon
            name="caret-right"
            size={14}
            color={styles.textStyle.color}
            style={styles.caretIconStyle}
          />
        </PressableWithOpacityFeedback>

        {/* Delivery methods - info badges (not interactive) */}
        <View style={styles.methodsContainerStyle}>
          <View style={[styles.chipStyle, !canPickup && styles.chipDisabledStyle]}>
            <View style={styles.chipLabelStyle}>
              <Icon
                name="bag-shopping"
                size={11}
                color={canPickup ? styles.chipTextStyle.color : styles.chipDisabledTextStyle.color}
              />
              <Text
                variant="body2"
                style={canPickup ? styles.chipTextStyle : styles.chipDisabledTextStyle}>
                Pick up
              </Text>
            </View>
            {canPickup ? (
              <View style={styles.chipValueStyle}>
                <Icon name="check" size={11} color={styles.chipTextStyle.color} />
              </View>
            ) : null}
          </View>
          <View style={[styles.chipStyle, !canDeliver && styles.chipDisabledStyle]}>
            <View style={styles.chipLabelStyle}>
              <Icon
                name="truck"
                size={12}
                color={canDeliver ? styles.chipTextStyle.color : styles.chipDisabledTextStyle.color}
              />
              <Text
                variant="body2"
                style={canDeliver ? styles.chipTextStyle : styles.chipDisabledTextStyle}>
                Delivery
              </Text>
            </View>
            {canDeliver ? (
              <View style={styles.chipValueStyle}>
                <Icon name="check" size={11} color={styles.chipTextStyle.color} />
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </View>
  );
}

const useStyles = makeStyles(theme => {
  const textColor =
    theme.colors?.legacy?.icon?.header?.secondary || theme.colors?.legacy?.icon?.header?.primary;

  return {
    containerStyle: {
      maxWidth: 1320,
      width: '100%',
      alignSelf: 'center',
    },
    wrapperStyle: {
      paddingVertical: 14,
      paddingHorizontal: 16,
      marginVertical: 10,
      marginHorizontal: 16,
      backgroundColor: theme.colors.primaryDarker,
      borderRadius: 8,
      alignItems: 'flex-start',
    },
    // Store name row - tappable with refined shadow
    storeContainerStyle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      backgroundColor: theme.colors.primaryDarkest,
      paddingVertical: 11,
      paddingHorizontal: 14,
      borderRadius: 6,
      // Refined shadow - subtle but visible
      shadowColor: '#000',
      shadowOffset: {width: 0, height: 1},
      shadowOpacity: 0.12,
      shadowRadius: 3,
      elevation: 2,
    },
    storeNameTextStyle: {
      flex: 1,
      flexShrink: 1,
      fontSize: 14,
    },
    caretIconStyle: {
      flexShrink: 0,
    },
    textStyle: {
      fontWeight: '600',
      color: textColor,
    },
    chipStyle: {
      flexDirection: 'row',
      alignItems: 'stretch',
      backgroundColor: theme.colors.primaryDarkest,
      borderRadius: 4,
      overflow: 'hidden',
    },
    chipDisabledStyle: {
      backgroundColor: theme.colors.primaryDarker,
    },
    chipLabelStyle: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: 10,
    },
    chipValueStyle: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 8,
      backgroundColor: 'rgba(0,0,0,0.05)',
    },
    chipTextStyle: {
      fontSize: 12,
      fontWeight: '600',
      color: textColor || '#ffffff',
    },
    chipDisabledTextStyle: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.colors.grey4,
    },
    methodsContainerStyle: {
      flexDirection: 'row',
      gap: 10,
      marginTop: 10,
    },
  };
});

export default StoreNameCard;
